import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import GlobalSearch from "@/components/GlobalSearch";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard, Users, Car, Package, Truck, LogOut, Plus, Search, Layers, UserCog, KeyRound, GitMerge, History, PackageCheck, Menu, ShoppingCart, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/por-encomendar", label: "Por encomendar", icon: ShoppingCart },
  { to: "/encomendas", label: "Encomendas", icon: Layers },
  { to: "/notas-credito", label: "Notas de crédito", icon: Receipt },
  { to: "/logistica", label: "Logística", icon: PackageCheck },
  { to: "/clientes", label: "Clientes", icon: Users },
  { to: "/veiculos", label: "Veículos", icon: Car },
  { to: "/artigos", label: "Artigos", icon: Package },
  { to: "/fornecedores", label: "Fornecedores", icon: Truck },
  { to: "/duplicados", label: "Duplicados", icon: GitMerge },
  { to: "/historicos", label: "Históricos", icon: History },
];

const ROLE_LABEL = { admin: "Admin", operador: "Operador", leitura: "Leitura" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [sideOpen, setSideOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      {sideOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setSideOpen(false)} data-testid="sidebar-overlay" />
      )}
      <aside className={`w-56 shrink-0 bg-slate-900 text-slate-200 flex flex-col fixed h-full z-40 transition-transform lg:translate-x-0 ${sideOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="h-14 flex items-center gap-2 px-4 border-b border-slate-800">
          <div className="h-7 w-7 rounded bg-sky-500 flex items-center justify-center font-bold text-white text-sm">A</div>
          <div>
            <div className="text-sm font-bold tracking-tight leading-none">ATLAS</div>
            <div className="text-[10px] text-slate-400 leading-none mt-0.5">by Blaublits</div>
          </div>
        </div>
        <nav className="flex-1 py-3 space-y-0.5 px-2" onClick={() => setSideOpen(false)}>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-sky-600 text-white font-medium" : "text-slate-300 hover:bg-slate-800"
                }`
              }
            >
              <n.icon size={17} />
              {n.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/utilizadores"
              data-testid="nav-utilizadores"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive ? "bg-sky-600 text-white font-medium" : "text-slate-300 hover:bg-slate-800"
                }`
              }
            >
              <UserCog size={17} />
              Utilizadores
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="text-xs font-medium text-white truncate">{user?.name}</div>
          <div className="text-[11px] text-slate-400 mb-2">{ROLE_LABEL[user?.role] || user?.role}</div>
          <button
            onClick={() => setPwOpen(true)}
            data-testid="btn-change-password"
            className="flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors mb-2"
          >
            <KeyRound size={14} /> Alterar palavra-passe
          </button>
          <button
            onClick={logout}
            data-testid="btn-logout"
            className="flex items-center gap-2 text-xs text-slate-300 hover:text-white transition-colors"
          >
            <LogOut size={14} /> Terminar sessão
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-56 flex flex-col min-h-screen min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center gap-3 px-4 sm:px-5 sticky top-0 z-10">
          <button
            onClick={() => setSideOpen(true)}
            data-testid="btn-menu-mobile"
            className="lg:hidden text-slate-600 hover:text-slate-900"
          >
            <Menu size={20} />
          </button>
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-trigger"
            className="flex items-center gap-2 text-sm text-slate-400 bg-slate-100 hover:bg-slate-200 transition-colors rounded-md px-3 py-1.5 w-40 sm:w-72"
          >
            <Search size={15} />
            <span>Pesquisa global…</span>
            <kbd className="ml-auto text-[10px] bg-white border border-slate-300 rounded px-1">Ctrl K</kbd>
          </button>
          <div className="ml-auto">
            <Button
              data-testid="btn-novo-pedido-top"
              onClick={() => navigate("/pedido/novo")}
              className="bg-sky-600 hover:bg-sky-700 h-9 gap-1.5"
            >
              <Plus size={16} /> Novo Pedido
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-5 overflow-x-hidden">{children}</main>
      </div>

      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
      <ChangePasswordDialog open={pwOpen} setOpen={setPwOpen} />
    </div>
  );
}
